import { useInfiniteQuery } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { useState } from 'react'

import { Button } from '@/components/elements/button'
import { Input } from '@/components/elements/input'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/elements/sheet'
import { Skeleton } from '@/components/elements/skeleton'
import CreateAssetModal from '@/pages/CourseAssets/CreateAssetModal'
import { getCourseAssets } from '@/services/course_assets.service'

/**
 * AssetPicker — pick an existing CourseAsset or upload a new one.
 *
 * Designed to drop into a content block form where the asset is bound by id.
 * Calls onChange(asset) when the user picks one (so the caller can derive
 * both the FK and any display fields it wants to mirror).
 */
export const AssetPicker = ({
  entityId,
  organizationId,
  type,
  value,
  valueLabel,
  onChange,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false)
  const [showCreate, setShowCreate] = useState(false)
  const [search, setSearch] = useState('')

  // Custom (facility / org) scope: only offer that scope's own assets. Without
  // this filter the facility endpoint would also return global RescueX +
  // org-shared assets (it unions them in), which the picker shouldn't surface
  // to a custom-course author building their own module.
  const isCustom = Boolean(entityId || organizationId)
  const filters = {
    ...(type ? { type } : {}),
    ...(isCustom ? { is_xverse_asset: false } : {}),
  }
  // Xverse scope already has more Video assets than one page; without paging
  // the tail of the list is unreachable except by search. Changing the search
  // term changes the query key, which resets the pages automatically.
  const { data, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['course-assets', entityId ?? organizationId ?? null, 'picker', type, search],
    queryFn: ({ pageParam }) =>
      getCourseAssets({
        entityId,
        organizationId,
        searchTerm: search || null,
        filters,
        pagination: { pageSize: 50, pageIndex: pageParam },
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const fetched = allPages.reduce((n, page) => n + page.results.length, 0)
      return fetched < lastPage.count ? allPages.length : undefined
    },
    enabled: open,
  })
  const results = data?.pages?.flatMap((page) => page.results) ?? []
  const total = data?.pages?.[0]?.count ?? 0

  const pick = (asset) => {
    onChange?.(asset)
    setOpen(false)
  }

  const handleCreated = (asset) => {
    setShowCreate(false)
    pick(asset)
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row items-center gap-2">
        {value ? (
          <div className="flex flex-1 flex-col">
            <span className="text-sm font-medium">{valueLabel || value}</span>
            <span className="text-muted-foreground text-xs">Asset attached</span>
          </div>
        ) : (
          <span className="text-muted-foreground flex-1 text-sm">No asset attached</span>
        )}
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() => setOpen(true)}
        >
          {value ? 'Replace' : 'Pick asset'}
        </Button>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>Pick {type ? `a ${type}` : 'an'} asset</SheetTitle>
            <SheetDescription>
              Select an existing asset or upload a new one. Updates to an asset propagate to every
              course that uses it.
            </SheetDescription>
          </SheetHeader>

          {/* SheetContent is a fixed h-full flex column with no overflow handling,
              so this section must claim the remaining height (min-h-0 flex-1) and
              delegate scrolling to the results list below — otherwise a long list
              clips at the viewport edge with no way to scroll. */}
          <div className="mt-4 flex min-h-0 flex-1 flex-col gap-3 px-4 pb-4">
            <div className="flex flex-row gap-2">
              <Input
                type="search"
                placeholder="Search assets"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <Button type="button" onClick={() => setShowCreate(true)}>
                Upload new
              </Button>
            </div>

            {isPending && <Skeleton className="h-24 w-full" />}

            {!isPending && results.length === 0 && (
              <p className="text-muted-foreground text-sm">
                No matching assets. Use Upload new to create one.
              </p>
            )}

            <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto">
              {results.map((asset) => {
                const inUse = (asset.courseCount ?? 0) > 0
                return (
                  <button
                    key={asset.id}
                    type="button"
                    className="hover:bg-muted flex shrink-0 flex-col items-start rounded border px-3 py-2 text-left"
                    onClick={() => pick(asset)}
                  >
                    <span className="text-sm font-medium">{asset.name}</span>
                    <span className="text-muted-foreground text-xs">
                      {asset.type} · {asset.fileDisplayName ?? '—'}
                      {inUse && ` · used by ${asset.courseCount} course module(s)`}
                    </span>
                  </button>
                )
              })}

              {hasNextPage && (
                <Button
                  type="button"
                  variant="outline"
                  className="mt-1 shrink-0"
                  disabled={isFetchingNextPage}
                  onClick={() => fetchNextPage()}
                >
                  {isFetchingNextPage
                    ? 'Loading...'
                    : `Load more (${total - results.length} remaining)`}
                </Button>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {showCreate && (
        <CreateAssetModal
          entityId={entityId}
          organizationId={organizationId}
          close={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  )
}

AssetPicker.propTypes = {
  entityId: PropTypes.string,
  organizationId: PropTypes.string,
  type: PropTypes.string,
  value: PropTypes.string,
  valueLabel: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
}
